import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { X, Euro, Car, Shield, TrendingUp } from 'lucide-react';
import { resolveCoverageLevel } from '../../../utils/insuranceStatus.js';
import { useOrgCurrency } from '../../../hooks/useOrgCurrency.js';

// Les libelles de couverture sont resolus par t('insurance.coverageShort.<code>').

const COVERAGE_COLORS = {
  tous_risques: 'bg-green-100 text-green-700',
  tiers_plus: 'bg-blue-100 text-blue-700',
  tiers_simple: 'bg-gray-100 text-gray-700',
  standard: 'bg-purple-100 text-purple-700',
};

function PremiumDetailModal({ vehicleInsurances, vehicles, contracts, coverageModels = [], insuredVehicleCount, onClose }) {
  const { t } = useTranslation();
  // Les primes viennent de filiales différentes : chaque sous-total est CONVERTI
  // dans la devise du périmètre avant addition (jamais un cumul brut EUR+PLN+SEK).
  const { money, moneyScope, sumScope } = useOrgCurrency();

  const getVehicle = (id) => vehicles.find(v => v.id === id);
  const getContract = (id) => contracts.find(c => c.id === id);

  const total = useMemo(
    () => sumScope(vehicleInsurances, vi => vi.premium_amount),
    [vehicleInsurances, sumScope]
  );
  const avgPremium = total.complete && insuredVehicleCount > 0 ? total.value / insuredVehicleCount : null;

  const byContract = useMemo(() => {
    const groups = {};
    vehicleInsurances.forEach(vi => {
      const key = vi.contract_id || 0;
      if (!groups[key]) groups[key] = { contractId: key, rows: [], vehicleCount: 0 };
      groups[key].rows.push(vi);
      groups[key].vehicleCount++;
    });
    return Object.values(groups)
      .map(g => ({ ...g, premium: sumScope(g.rows, vi => vi.premium_amount) }))
      .sort((a, b) => b.premium.value - a.premium.value);
  }, [vehicleInsurances, sumScope]);

  const byCoverage = useMemo(() => {
    const groups = {};
    vehicleInsurances.forEach(vi => {
      const key = resolveCoverageLevel(vi, coverageModels) || 'standard';
      if (!groups[key]) groups[key] = { coverage: key, rows: [], count: 0 };
      groups[key].rows.push(vi);
      groups[key].count++;
    });
    return Object.values(groups)
      .map(g => ({ ...g, premium: sumScope(g.rows, vi => vi.premium_amount) }))
      .sort((a, b) => b.premium.value - a.premium.value);
  }, [vehicleInsurances, coverageModels, sumScope]);

  const topVehicles = useMemo(() => {
    return [...vehicleInsurances]
      .sort((a, b) => (b.premium_amount || 0) - (a.premium_amount || 0))
      .slice(0, 10);
  }, [vehicleInsurances]);

  const monthlyEquiv = total.complete ? total.value / 12 : null;
  const dailyCost = total.complete ? total.value / 365 : null;

  return (
    <div
      className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && onClose()}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-[900px] max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 rounded-t-xl flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Euro className="w-6 h-6 text-white" />
            <h2 className="text-lg font-semibold text-white">
              {t('insurance.premiumModal.title', { amount: total.label })}
            </h2>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white transition-colors">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* 3 Mini Summary Cards */}
          <div className="grid grid-cols-3 gap-4">
            <div className="bg-blue-50 rounded-lg p-4 text-center border border-blue-100">
              <p className="text-2xl font-bold text-blue-700">{total.label}</p>
              <p className="text-xs text-blue-500">{t('insurance.premiumModal.totalPremium')}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
              <p className="text-2xl font-bold text-gray-900">{insuredVehicleCount}</p>
              <p className="text-xs text-gray-500">{t('insurance.premiumModal.insuredVehicles')}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4 text-center border border-gray-200">
              <p className="text-2xl font-bold text-gray-900">{avgPremium != null ? moneyScope(avgPremium) : '—'}</p>
              <p className="text-xs text-gray-500">{t('insurance.premiumModal.avgPremium')}</p>
            </div>
          </div>

          {/* By Contract Table */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
              <Shield className="w-4 h-4" />
              {t('insurance.premiumModal.byContract')}
            </h3>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
                    <th className="pb-2 pr-4">{t('insurance.premiumModal.col.contract')}</th>
                    <th className="pb-2 pr-4">{t('insurance.premiumModal.col.insurer')}</th>
                    <th className="pb-2 pr-4 text-center">{t('insurance.premiumModal.col.vehicles')}</th>
                    <th className="pb-2 pr-4 text-right">{t('insurance.premiumModal.col.premium')}</th>
                    <th className="pb-2 pr-4 text-right">{t('insurance.premiumModal.col.share')}</th>
                    <th className="pb-2"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {byContract.map(({ contractId, premium, vehicleCount }) => {
                    const contract = getContract(contractId);
                    const pct = total.value > 0 ? (premium.value / total.value) * 100 : 0;
                    return (
                      <tr key={contractId} className="hover:bg-gray-50">
                        <td className="py-2.5 pr-4 font-medium text-gray-900">
                          {contract?.policy_number || '—'}
                        </td>
                        <td className="py-2.5 pr-4 text-gray-600">
                          {contract?.insurer_name || '—'}
                        </td>
                        <td className="py-2.5 pr-4 text-center text-blue-600 font-medium">
                          {vehicleCount}
                        </td>
                        <td className="py-2.5 pr-4 text-right font-medium text-gray-900">
                          {premium.label}
                        </td>
                        <td className="py-2.5 pr-4 text-right text-gray-500">
                          {pct.toFixed(1)}%
                        </td>
                        <td className="py-2.5 w-24">
                          <div className="w-full bg-gray-200 rounded-full h-2">
                            <div
                              className="bg-blue-500 h-2 rounded-full transition-all"
                              style={{ width: `${Math.min(pct, 100)}%` }}
                            ></div>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>

          {/* By Coverage Type */}
          <div>
            <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
              <TrendingUp className="w-4 h-4" />
              {t('insurance.premiumModal.byCoverageType')}
            </h3>
            <div className="grid grid-cols-3 gap-3">
              {byCoverage.map(({ coverage, premium, count }) => {
                const label = t(`insurance.coverageShort.${coverage}`, { defaultValue: coverage });
                const colorClass = COVERAGE_COLORS[coverage] || COVERAGE_COLORS.standard;
                return (
                  <div key={coverage} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${colorClass}`}>
                        {label}
                      </span>
                      <span className="text-xs text-gray-500">{t('insurance.premiumModal.vehicleCount', { count })}</span>
                    </div>
                    <p className="text-lg font-bold text-gray-900">{premium.label}</p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Top 10 Most Expensive Vehicles */}
          {topVehicles.length > 0 && (
            <div>
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3 flex items-center gap-2">
                <Car className="w-4 h-4" />
                {t('insurance.premiumModal.topVehicles', { n: Math.min(topVehicles.length, 10) })}
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-xs text-gray-500 uppercase tracking-wider border-b border-gray-200">
                      <th className="pb-2 pr-4">#</th>
                      <th className="pb-2 pr-4">{t('insurance.premiumModal.col.plate')}</th>
                      <th className="pb-2 pr-4">{t('insurance.premiumModal.col.makeModel')}</th>
                      <th className="pb-2 pr-4">{t('insurance.premiumModal.col.contract')}</th>
                      <th className="pb-2 pr-4">{t('insurance.premiumModal.col.coverage')}</th>
                      <th className="pb-2 text-right">{t('insurance.premiumModal.col.premium')}</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {topVehicles.map((vi, idx) => {
                      const vehicle = getVehicle(vi.vehicle_id);
                      const contract = getContract(vi.contract_id);
                      const coverageKey = resolveCoverageLevel(vi, coverageModels) || 'standard';
                      return (
                        <tr key={vi.id} className="hover:bg-gray-50">
                          <td className="py-2.5 pr-4 text-gray-400 font-medium">{idx + 1}</td>
                          <td className="py-2.5 pr-4 font-medium text-gray-900">
                            {vehicle?.license_plate || t('common.states.notAvailable')}
                          </td>
                          <td className="py-2.5 pr-4 text-gray-600">
                            {vehicle ? `${vehicle.make || ''} ${vehicle.model || ''}`.trim() || '—' : '—'}
                          </td>
                          <td className="py-2.5 pr-4 text-gray-600">
                            {contract?.policy_number || '—'}
                          </td>
                          <td className="py-2.5 pr-4">
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${COVERAGE_COLORS[coverageKey] || COVERAGE_COLORS.standard}`}>
                              {t(`insurance.coverageShort.${coverageKey}`, { defaultValue: coverageKey })}
                            </span>
                          </td>
                          <td className="py-2.5 text-right font-semibold text-gray-900">
                            {money(vi.premium_amount, vi)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Footer */}
          <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
            <div className="flex items-center justify-between text-sm">
              <span className="text-gray-600">
                {t('insurance.premiumModal.monthlyEquivalent')} <span className="font-semibold text-gray-900">{monthlyEquiv != null ? moneyScope(monthlyEquiv) : '—'}</span>
              </span>
              <span className="text-gray-600">
                {t('insurance.premiumModal.dailyCost')} <span className="font-semibold text-gray-900">{dailyCost != null ? moneyScope(dailyCost) : '—'}</span>
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PremiumDetailModal;
